import L from "leaflet";
import "leaflet/dist/leaflet.css";

const COUNTRY_SPECIAL_CASES = {
    'germany': ['deutschland', 'federal republic of germany'],
    'uk': ['united kingdom', 'great britain', 'england', 'scotland', 'wales', 'northern ireland'],
    'united kingdom': ['uk', 'great britain', 'england', 'scotland', 'wales', 'northern ireland'],
    'usa': ['united states', 'united states of america'],
    'united states': ['usa', 'united states of america'],
    'netherlands': ['the netherlands', 'nederland', 'holland'],
    'switzerland': ['schweiz', 'suisse', 'svizzera']
};

const COUNTRIES_GEOJSON_URL = 'https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson';
const COUNTRIES_GEOJSON_FALLBACK_URL = 'https://cdn.jsdelivr.net/gh/johan/world.geo.json@master/countries.geo.json';

const MAX_RETRIES = 50; // Max 5 seconds of retrying (50 * 100ms)

export default class FacilitiesMapHandler {
    constructor() {
        this.maps = [];
    }

    init() {
        const sections = document.querySelectorAll('[data-taxi-component="facilitiesMapModule"]');

        sections.forEach((section) => {
            const data = (window.facilitiesMapData || {})[section.id];
            if (!data) return;

            const mapElement = document.getElementById(data.mapId);
            if (!mapElement || mapElement.dataset.mapInitialized) return;

            this.createMap(section, mapElement, data, 0);
        });
    }

    createMap(section, mapElement, data, retryCount) {
        const containerRect = mapElement.getBoundingClientRect();
        if (containerRect.width === 0 || containerRect.height === 0) {
            if (retryCount < MAX_RETRIES) {
                setTimeout(() => this.createMap(section, mapElement, data, retryCount + 1), 100);
            }
            return;
        }

        mapElement.dataset.mapInitialized = 'true';

        // Create map with user-friendly zoom controls (scroll disabled for better page UX)
        const map = L.map(mapElement, {
            center: [45, 0], // Start with world view
            zoom: 4, // World view initially
            scrollWheelZoom: false,  // Disable scroll wheel zoom to prevent scroll trap
            zoomControl: true,       // Enable zoom control buttons
            doubleClickZoom: true,   // Enable double click zoom
            touchZoom: true,         // Enable pinch zoom on mobile
            boxZoom: false,          // Keep box zoom disabled (less commonly used)
            keyboard: true,          // Enable keyboard zoom (+ and - keys)
            minZoom: 1,
            maxZoom: 18,
            worldCopyJump: true,
            preferCanvas: false // Keep SVG for better rendering quality
        });

        this.maps.push(map);

        // Add resize observer to handle container size changes
        if (window.ResizeObserver) {
            const resizeObserver = new ResizeObserver(function () {
                map.invalidateSize();
            });
            resizeObserver.observe(mapElement);
        }

        // Invalidate size immediately after creation
        setTimeout(function () {
            map.invalidateSize();
        }, 0);

        // Add a very light base layer
        // let baseLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
        //     attribution: '',
        //     subdomains: 'abcd',
        //     maxZoom: 19,
        //     opacity: 0.3
        // }).addTo(map);

        // Custom icon with proper anchor point
        const customIcon = L.divIcon({
            className: 'custom-map-marker',
            html: '<div class="marker-pin"><div class="marker-inner"></div></div>',
            iconSize: [30, 40],
            iconAnchor: [15, 40], // Point tip of the pin
            popupAnchor: [0, -40]
        });

        // Create markers layer first (before loading countries)
        const markers = [];
        const markersLayer = new L.LayerGroup();
        map.addLayer(markersLayer);

        data.facilities.forEach(function (facility, index) {
            if (!facility.lat || !facility.lng) return;

            const marker = L.marker([facility.lat, facility.lng], {
                icon: customIcon,
                title: facility.title
            });

            // Create popup
            const typeLabel = facility.type === 'headquarters' ? 'Headquarters' : 'Office';
            const displayTitle = facility.office || facility.title;

            let popupContent = '<div class="text-sm text-dark-green">' +
                '<span class="text-sm text-purple">' + typeLabel + '</span>' +
                '<h3 class="text-sm text-dark-green">' + displayTitle + '</h3>' +
                '</div>';

            if (facility.address) {
                popupContent += '<div class="flex flex-col"><h4 class="text-sm text-purple">Address</h4><div class="text-sm text-dark-green !m-0">' + facility.address + '</p></div>';
            }

            if (facility.services && facility.services.length > 0) {
                popupContent += '<h4 class="text-sm text-purple">Services</h4><ul class="text-sm">';
                facility.services.forEach(function (service) {
                    if (service.url) {
                        popupContent += '<li><a href="' + service.url + '" target="' + service.target + '" class="!text-dark-green hover:underline">' + service.title + '</a></li>';
                    } else {
                        popupContent += '<li>' + service.title + '</li>';
                    }
                });
                popupContent += '</ul>';
            }

            marker.bindPopup(popupContent, {
                className: 'facility-popup',
                maxWidth: 320
            });

            // Store data
            marker.facilityData = facility;
            marker.facilityIndex = index;

            // Add to map
            marker.addTo(markersLayer);
            markers.push(marker);
        });

        // Create a set of countries that have facilities
        const countriesWithFacilities = {};
        let activeCountry = null;

        // Process facilities and extract countries
        data.facilities.forEach(function (facility) {
            if (facility.country) {
                let country = facility.country.toLowerCase().trim();
                // Remove any remaining whitespace or special characters
                country = country.replace(/^\s+|\s+$/g, '');

                if (country && country !== '') {
                    if (!countriesWithFacilities[country]) {
                        countriesWithFacilities[country] = [];
                    }
                    countriesWithFacilities[country].push(facility);
                }
            }
        });

        // Load world countries GeoJSON and style based on facility presence
        let countriesLayer;

        // Wait for map to be ready before loading countries
        map.whenReady(function () {
            // First, add a simple world outline
            fetch(COUNTRIES_GEOJSON_URL)
                .then(function (response) {
                    if (!response.ok) throw new Error('Failed to load countries');
                    return response.json();
                })
                .then(function (countriesData) {
                    // Create normalized lookup for facility countries
                    const facilityCountriesNormalized = {};
                    Object.keys(countriesWithFacilities).forEach(function (country) {
                        const normalized = country.toLowerCase().trim();
                        facilityCountriesNormalized[normalized] = countriesWithFacilities[country];
                    });

                    // Render the countries
                    countriesLayer = L.geoJSON(countriesData, {
                        style: function (feature) {
                            const countryNameOriginal = feature.properties.name || feature.properties.NAME || '';
                            const countryName = countryNameOriginal.toLowerCase().trim();
                            let hasFactories = false;

                            // Direct check for exact country name match
                            if (facilityCountriesNormalized[countryName]) {
                                hasFactories = true;
                            }

                            // Check with special variations for commonly mismatched countries
                            if (!hasFactories) {
                                // Check if the GeoJSON country name matches any facility country through special cases
                                Object.keys(facilityCountriesNormalized).forEach(function (facilityCountry) {
                                    if (COUNTRY_SPECIAL_CASES[facilityCountry]) {
                                        COUNTRY_SPECIAL_CASES[facilityCountry].forEach(function (variation) {
                                            if (countryName === variation || countryName.includes(variation)) {
                                                hasFactories = true;
                                            }
                                        });
                                    }
                                    // Also check reverse - if GeoJSON country has special cases that match facility
                                    if (COUNTRY_SPECIAL_CASES[countryName]) {
                                        COUNTRY_SPECIAL_CASES[countryName].forEach(function (variation) {
                                            if (facilityCountry === variation) {
                                                hasFactories = true;
                                            }
                                        });
                                    }
                                });
                            }

                            // Apply styles with stronger colors
                            if (activeCountry === countryName) {
                                return {
                                    fillColor: data.colorScheme.countries_active,
                                    fillOpacity: 1,
                                    opacity: 0
                                };
                            } else if (hasFactories) {
                                return {
                                    fillColor: data.colorScheme.countries_with_facilities,
                                    fillOpacity: 1,
                                    opacity: 0
                                };
                            } else {
                                return {
                                    fillColor: data.colorScheme.countries_default,
                                    fillOpacity: 1,
                                    opacity: 0
                                };
                            }
                        },
                        onEachFeature: function (feature, layer) {
                            const countryName = feature.properties.name || '';
                            const countryNameLower = countryName.toLowerCase().trim();

                            // Find matching facilities
                            let facilities = [];

                            // Direct match first
                            if (countriesWithFacilities[countryNameLower]) {
                                facilities = countriesWithFacilities[countryNameLower];
                            }

                            // Check special cases if no direct match
                            if (facilities.length === 0) {
                                Object.keys(countriesWithFacilities).forEach(function (facilityCountry) {
                                    if (COUNTRY_SPECIAL_CASES[facilityCountry]) {
                                        COUNTRY_SPECIAL_CASES[facilityCountry].forEach(function (variation) {
                                            if (countryNameLower === variation || countryNameLower.includes(variation)) {
                                                facilities = countriesWithFacilities[facilityCountry];
                                            }
                                        });
                                    }
                                    if (COUNTRY_SPECIAL_CASES[countryNameLower]) {
                                        COUNTRY_SPECIAL_CASES[countryNameLower].forEach(function (variation) {
                                            if (facilityCountry === variation) {
                                                facilities = countriesWithFacilities[facilityCountry];
                                            }
                                        });
                                    }
                                });
                            }

                            if (facilities.length > 0) {
                                layer.on({
                                    mouseover: function (e) {
                                        if (activeCountry !== countryNameLower) {
                                            e.target.setStyle({
                                                fillOpacity: 0.95,
                                                weight: 2
                                            });
                                        }
                                        e.target.bringToFront();
                                    },
                                    mouseout: function (e) {
                                        countriesLayer.resetStyle(e.target);
                                    },
                                    click: function (e) {
                                        // Update active country
                                        activeCountry = countryNameLower;

                                        // Reset all country styles
                                        countriesLayer.eachLayer(function (layer) {
                                            countriesLayer.resetStyle(layer);
                                        });

                                        // Set active country color
                                        e.target.setStyle({
                                            fillColor: data.colorScheme.countries_active,
                                            fillOpacity: 0.95,
                                            weight: 3
                                        });

                                        // Filter markers to show only this country's facilities
                                        markers.forEach(function (marker) {
                                            const markerCountry = marker.facilityData.country;
                                            let showMarker = false;

                                            // Direct match
                                            if (markerCountry === countryNameLower) {
                                                showMarker = true;
                                            }

                                            // Check special cases
                                            if (!showMarker) {
                                                if (COUNTRY_SPECIAL_CASES[markerCountry]) {
                                                    COUNTRY_SPECIAL_CASES[markerCountry].forEach(function (variation) {
                                                        if (countryNameLower === variation || countryNameLower.includes(variation)) {
                                                            showMarker = true;
                                                        }
                                                    });
                                                }
                                                if (COUNTRY_SPECIAL_CASES[countryNameLower]) {
                                                    COUNTRY_SPECIAL_CASES[countryNameLower].forEach(function (variation) {
                                                        if (markerCountry === variation) {
                                                            showMarker = true;
                                                        }
                                                    });
                                                }
                                            }

                                            if (showMarker) {
                                                if (!markersLayer.hasLayer(marker)) {
                                                    marker.addTo(markersLayer);
                                                }
                                            } else {
                                                markersLayer.removeLayer(marker);
                                            }
                                        });
                                    }
                                });

                                // Add tooltip for country info (shows on hover)
                                layer.bindPopup('<strong>' + countryName + '</strong><br>' +
                                    facilities.length + ' location(s)', {
                                        permanent: false,
                                        direction: 'top',
                                        className: 'country-tooltip'
                                    });
                            }
                        }
                    });

                    // Add to map and ensure it renders
                    countriesLayer.addTo(map);

                    // Force SVG to update its viewBox after adding GeoJSON
                    let svgElement = map.getPanes().overlayPane.querySelector('svg');
                    if (svgElement) {
                        const mapSize = map.getSize();
                        svgElement.setAttribute('viewBox', '0 0 ' + mapSize.x + ' ' + mapSize.y);
                        svgElement.style.width = mapSize.x + 'px';
                        svgElement.style.height = mapSize.y + 'px';
                    }

                    // Force proper rendering with multiple invalidateSize calls
                    // First call immediately
                    map.invalidateSize(false);

                    // Second call after a short delay to catch any CSS transitions
                    setTimeout(function () {
                        map.invalidateSize(true); // true = animate the size change

                        // Trigger a pan to force redraw of all layers
                        map.panBy([1, 0]);
                        map.panBy([-1, 0]);

                        // Check SVG dimensions again
                        const svgElement = map.getPanes().overlayPane.querySelector('svg');
                        if (svgElement) {
                            const mapSize = map.getSize();
                            if (!svgElement.getAttribute('viewBox')) {
                                svgElement.setAttribute('viewBox', '0 0 ' + mapSize.x + ' ' + mapSize.y);
                            }
                        }
                    }, 200);

                    // Third call after potential images/fonts load
                    setTimeout(function () {
                        map.invalidateSize(false);
                    }, 500);

                    // Ensure markers stay on top
                    if (markersLayer) {
                        // Remove and re-add to ensure it's on top
                        map.removeLayer(markersLayer);
                        map.addLayer(markersLayer);
                    }
                })
                .catch(function (error) {
                    // Try alternative GeoJSON source via CDN with CORS support
                    return fetch(COUNTRIES_GEOJSON_FALLBACK_URL)
                        .then(function (response) {
                            if (!response.ok) throw new Error('Fallback also failed');
                            return response.json();
                        })
                        .then(function (countriesData) {
                            // Same processing logic as above
                            const facilityCountriesNormalized = {};
                            Object.keys(countriesWithFacilities).forEach(function (country) {
                                const normalized = country.toLowerCase().trim();
                                facilityCountriesNormalized[normalized] = countriesWithFacilities[country];
                            });

                            countriesLayer = L.geoJSON(countriesData, {
                                style: function (feature) {
                                    const countryNameOriginal = feature.properties.ADMIN || feature.properties.name || '';
                                    const countryName = countryNameOriginal.toLowerCase().trim();
                                    let hasFactories = false;

                                    // Direct check for exact country name match
                                    if (facilityCountriesNormalized[countryName]) {
                                        hasFactories = true;
                                    }

                                    // Check with special variations for commonly mismatched countries
                                    if (!hasFactories) {
                                        Object.keys(facilityCountriesNormalized).forEach(function (facilityCountry) {
                                            if (COUNTRY_SPECIAL_CASES[facilityCountry]) {
                                                COUNTRY_SPECIAL_CASES[facilityCountry].forEach(function (variation) {
                                                    if (countryName === variation || countryName.includes(variation)) {
                                                        hasFactories = true;
                                                    }
                                                });
                                            }
                                            if (COUNTRY_SPECIAL_CASES[countryName]) {
                                                COUNTRY_SPECIAL_CASES[countryName].forEach(function (variation) {
                                                    if (facilityCountry === variation) {
                                                        hasFactories = true;
                                                    }
                                                });
                                            }
                                        });
                                    }

                                    if (activeCountry === countryName) {
                                        return {
                                            fillColor: data.colorScheme.countries_active,
                                            fillOpacity: 1,
                                            opacity: 0
                                        };
                                    } else if (hasFactories) {
                                        return {
                                            fillColor: data.colorScheme.countries_with_facilities,
                                            fillOpacity: 1,
                                            opacity: 0
                                        };
                                    } else {
                                        return {
                                            fillColor: data.colorScheme.countries_default,
                                            fillOpacity: 0.8,
                                            opacity: 0
                                        };
                                    }
                                }
                            });

                            countriesLayer.addTo(map);

                            // Add base layer if not exists
                            if (!baseLayer) {
                                baseLayer = null;
                            //     baseLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
                            //         attribution: '',
                            //         subdomains: 'abcd',
                            //         maxZoom: 19,
                            //         opacity: 0.1
                            //     });
                            //     baseLayer.addTo(map);
                            //     baseLayer.setZIndex(0);
                            }

                            // Set z-index for proper layering
                            if (countriesLayer.setZIndex) {
                                countriesLayer.setZIndex(10);
                            }

                            // Ensure markers stay on top
                            if (markersLayer) {
                                // Remove and re-add to ensure it's on top
                                map.removeLayer(markersLayer);
                                map.addLayer(markersLayer);
                            }
                        })
                        .catch(function (fallbackError) {
                            // Show simplified message, but remove after 3 seconds
                            const errorDiv = L.control({ position: 'topright' });
                            errorDiv.onAdd = function () {
                                const div = L.DomUtil.create('div', 'map-message');
                                div.innerHTML = 'Loading map boundaries...';
                                div.style.padding = '8px 12px';
                                div.style.background = 'rgba(255, 255, 255, 0.9)';
                                div.style.borderRadius = '4px';
                                div.style.fontSize = '12px';
                                div.style.color = '#6B7280';
                                return div;
                            };
                            const control = errorDiv.addTo(map);

                            // Remove message after 3 seconds
                            setTimeout(function () {
                                map.removeControl(control);
                            }, 3000);
                        });
                });
        }); // End of map.whenReady

        // Store map instance for taxi.js
        section.facilitiesMap = map;
    }

    destroy() {
        this.maps.forEach(function (map) {
            map.remove();
        });
        this.maps = [];

        document.querySelectorAll('.leaflet-map[data-map-initialized]').forEach(function (el) {
            delete el.dataset.mapInitialized;
        });
    }
}
